import React, { useState, useEffect } from 'react';

const VehicleForm = ({ onClose, vehicle = {} }) => {
  const [formData, setFormData] = useState({
    vehicle_number: vehicle.vehicle_number || '',
    vehicle_type: vehicle.vehicle_type || '',
    driver_name: vehicle.driver_name || '',
    driver_phone: vehicle.driver_phone || '',
    vendor: vehicle.vendor || '',
    plant_id: vehicle.plant_id || ''
  });

  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingPlants, setFetchingPlants] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchPlants = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/plants.php`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        const data = await response.json();
        if (data.success) {
          setPlants(data.data || []);
          if (vehicle.plant_id) {
            setFormData(prev => ({ ...prev, plant_id: vehicle.plant_id }));
          }
        } else {
          setError('Failed to load plants');
        }
      } catch (err) {
        setError('Error loading plants');
        console.error(err);
      } finally {
        setFetchingPlants(false);
      }
    };

    fetchPlants();
  }, [vehicle.plant_id]);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!formData.plant_id) {
      setError('Please select a starting plant');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const url = vehicle.id
        ? `${process.env.REACT_APP_API_BASE_URL}/vehicles.php`
        : `${process.env.REACT_APP_API_BASE_URL}/vehicles.php`;

      const method = vehicle.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(vehicle.id ? 'Vehicle updated successfully!' : 'Vehicle added successfully!');
        setTimeout(() => onClose(true), 1500);
      } else {
        setError(data.message || (vehicle.id ? 'Failed to update vehicle' : 'Failed to add vehicle'));
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* All CSS embedded here – no external file needed */}
      <style>{`
        .vehicle-form-container {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          overflow: hidden;
        }

        .vehicle-form {
          background: white;
          border-radius: 12px;
          width: 90%;
          max-width: 800px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 10px 40px rgba(0,0,0,0.3);
          position: relative;
        }

        .vehicle-form::-webkit-scrollbar {
          width: 8px;
        }

        .vehicle-form::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }

        .vehicle-form::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 4px;
        }

        .vehicle-form::-webkit-scrollbar-thumb:hover {
          background: #555;
        }

        .form-header {
          position: sticky;
          top: 0;
          background: white;
          z-index: 10;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
          padding: 20px 24px;
          border-bottom: 2px solid #f0f0f0;
        }

        .form-header h2 {
          margin: 0;
          color: #1e3c72;
          font-size: 1.8rem;
        }

        .btn-close {
          background: none;
          border: none;
          font-size: 1.8rem;
          cursor: pointer;
          color: #666;
          padding: 0;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: background-color 0.3s;
        }

        .btn-close:hover {
          background-color: #f5f5f5;
          color: #333;
        }

        .form-content {
          padding: 0 24px 24px 24px;
        }

        .form-group {
          margin-bottom: 25px;
        }

        .form-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 600;
          color: #333;
          font-size: 0.95rem;
        }

        .hint {
          font-size: 0.85rem;
          color: #666;
          font-weight: normal;
          margin-left: 8px;
        }

        .hint.danger {
          color: #dc3545;
        }

        .form-group input,
        .form-group select {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          font-size: 1rem;
          transition: all 0.3s;
          background-color: white;
          color: #333;
        }

        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: #1e3c72;
          box-shadow: 0 0 0 3px rgba(30, 60, 114, 0.1);
        }

        .form-group input::placeholder {
          color: #999;
        }

        .form-group input:disabled,
        .form-group select:disabled {
          background-color: #f5f5f5;
          cursor: not-allowed;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }

        .form-actions {
          position: sticky;
          bottom: 0;
          background: white;
          z-index: 10;
          display: flex;
          justify-content: flex-end;
          gap: 15px;
          margin-top: 40px;
          padding: 20px 24px;
          border-top: 2px solid #f0f0f0;
        }

        .btn-primary,
        .btn-secondary {
          padding: 12px 30px;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          min-width: 120px;
          justify-content: center;
        }

        .btn-primary {
          background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
          color: white;
          border: none;
        }

        .btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(30, 60, 114, 0.3);
        }

        .btn-primary:disabled {
          background: #cccccc;
          cursor: not-allowed;
        }

        .btn-secondary {
          background: white;
          color: #1e3c72;
          border: 2px solid #1e3c72;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #1e3c72;
          color: white;
        }

        .spinner {
          width: 18px;
          height: 18px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: white;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .alert {
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .alert-danger {
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }

        .alert-success {
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }

        .loading-text {
          color: #666;
          font-style: italic;
        }

        @media (max-width: 768px) {
          .form-row {
            grid-template-columns: 1fr;
            gap: 15px;
          }
          
          .form-actions {
            flex-direction: column;
            gap: 12px;
          }
          
          .btn-primary,
          .btn-secondary {
            width: 100%;
          }
        }
      `}</style>

      <div className="vehicle-form-container" onClick={(e) => {
        // Optional: close modal when clicking outside the form
        if (e.target === e.currentTarget) onClose(false);
      }}>
        <div className="vehicle-form">
          <div className="form-header">
            <h2>{vehicle.id ? 'Edit Vehicle' : 'Add New Vehicle'}</h2>
            <button className="btn-close" onClick={() => onClose(false)}>×</button>
          </div>

          <div className="form-content">
            {error && (
              <div className="alert alert-danger">
                <span>❌</span> {error}
              </div>
            )}

            {success && (
              <div className="alert alert-success">
                <span>✅</span> {success}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="vehicle_number">
                  Vehicle Number *
                  <span className="hint">(Unique identifier)</span>
                </label>
                <input
                  type="text"
                  id="vehicle_number"
                  name="vehicle_number"
                  value={formData.vehicle_number}
                  onChange={handleChange}
                  placeholder="e.g., MH12AB1234"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="vehicle_type">Vehicle Type</label>
                <select
                  id="vehicle_type"
                  name="vehicle_type"
                  value={formData.vehicle_type}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">Select type</option>
                  <option value="Truck">Truck</option>
                  <option value="Trailer">Trailer</option>
                  <option value="Van">Van</option>
                  <option value="Container">Container</option>
                  <option value="Bus">Bus</option>
                  <option value="Car">Car</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="driver_name">Driver Name</label>
                  <input
                    type="text"
                    id="driver_name"
                    name="driver_name"
                    value={formData.driver_name}
                    onChange={handleChange}
                    placeholder="Enter driver name"
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="driver_phone">Driver Phone</label>
                  <input
                    type="tel"
                    id="driver_phone"
                    name="driver_phone"
                    value={formData.driver_phone}
                    onChange={handleChange}
                    placeholder="Enter phone number"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="vendor">Vendor / Company</label>
                <input
                  type="text"
                  id="vendor"
                  name="vendor"
                  value={formData.vendor}
                  onChange={handleChange}
                  placeholder="Enter vendor/company name"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="plant_id">
                  Starting Plant *
                  <span className="hint">(Where the vehicle usually starts)</span>
                </label>
                {fetchingPlants ? (
                  <div className="loading-text">Loading plants...</div>
                ) : (
                  <select
                    id="plant_id"
                    name="plant_id"
                    value={formData.plant_id}
                    onChange={handleChange}
                    required
                    disabled={loading || plants.length === 0}
                  >
                    <option value="">Select a plant</option>
                    {plants.map(plant => (
                      <option key={plant.id} value={plant.id}>
                        {plant.plant_name} {plant.address ? `(${plant.address.substring(0, 30)}...)` : ''}
                      </option>
                    ))}
                  </select>
                )}
                {plants.length === 0 && !fetchingPlants && (
                  <small className="hint danger">No plants available. Please add plants first.</small>
                )}
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => onClose(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading || fetchingPlants || plants.length === 0}
                >
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      {vehicle.id ? 'Updating...' : 'Adding...'}
                    </>
                  ) : (
                    vehicle.id ? 'Update Vehicle' : 'Add Vehicle'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default VehicleForm;